import { DEFAULT_PRODUCT, type Product } from './brand'

/**
 * What an org is called where a reader meets the word: a congregation on the
 * shul product, an organization on any other. It names the org rather than
 * describing its luach, so it is a map of its own.
 *
 * With no product — a site no product owns, such as the platform's host or a
 * bare local address — the shul product's word stands, as it does wherever a
 * product is not named.
 */
const ORG_WORD: Record<Product, string> = {
  [DEFAULT_PRODUCT]: 'congregation',
  terumah: 'organization',
  studio: 'organization',
}

export function orgWordFor(product: Product | null): string {
  return product ? ORG_WORD[product] : 'congregation'
}

/**
 * What the office calls its own org in the back office: "the shul" on the
 * shul product, as a gabbai says it, and "the organization" anywhere else.
 * Apart from `orgWordFor`, which is the word a donor or a member reads.
 * Both forms are held, so no screen builds an article for itself.
 */
export type OfficeOrgNoun = { noun: string; withArticle: string }

const OFFICE_ORG_NOUN: Record<Product, OfficeOrgNoun> = {
  [DEFAULT_PRODUCT]: { noun: 'shul', withArticle: 'a shul' },
  terumah: { noun: 'organization', withArticle: 'an organization' },
  studio: { noun: 'organization', withArticle: 'an organization' },
}

export function officeOrgNounFor(product: Product | null): OfficeOrgNoun {
  return OFFICE_ORG_NOUN[product ?? DEFAULT_PRODUCT]
}

/**
 * Who a gift is to, as a donor reads it. A shul's says "the shul", as a
 * congregation says it of itself; elsewhere the org is named, because "the
 * organization" tells a donor nothing they did not know.
 *
 * A page reads it from the site being served and mail from the org's kind;
 * an org's pages are served on its own product's site, where the two agree.
 */
const GIFT_TO: Record<Product, (orgName: string) => string> = {
  [DEFAULT_PRODUCT]: () => 'the shul',
  terumah: (orgName) => orgName,
  studio: (orgName) => orgName,
}

export function giftToFor(product: Product | null, orgName: string): string {
  return product ? GIFT_TO[product](orgName) : 'the shul'
}

/**
 * Whether this product's letters speak in a membership's terms. Membership is
 * a shul's way of belonging — dues, a tier, a household's standing — so a
 * shul's letters reassure a member about it; a charity's speak of the donor's
 * account, which stays true where its office has chosen to offer a membership
 * too. With no product, the shul product's answer stands.
 */
export function holdsMemberships(product: Product | null): boolean {
  return product === null || product === DEFAULT_PRODUCT
}

/**
 * Whether an org made on this product starts with its statements sent every
 * month. A statement reaches every billing account with something to report,
 * on the org's own day, with nobody watching; a charity's donors are sent one only
 * when its office chooses, so a charity starts with the run off.
 */
const STARTS_WITH_MONTHLY_STATEMENTS: Record<Product, boolean> = {
  [DEFAULT_PRODUCT]: true,
  terumah: false,
  studio: false,
}

export function startsWithMonthlyStatements(product: Product): boolean {
  return STARTS_WITH_MONTHLY_STATEMENTS[product]
}
